import React, { useState } from 'react';
import { Card, Button, Badge, Modal, Input } from '../components/ui';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDatabase } from '../hooks/useDatabase';

const EMPTY = {
  titulo: '',
  cliente_id: '',
  trastero_id: '',
  tipo: 'mantenimiento',
  prioridad: 'media',
  estado: 'abierta',
  descripcion: '',
  notas_internas: ''
};

const Incidencias = () => {
  const { data, loading, error, refresh, insert, update, remove } = useDatabase('incidencias');
  const { data: clientes } = useDatabase('clientes');
  const { data: trasteros } = useDatabase('trasteros');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      titulo: item.titulo,
      cliente_id: item.cliente_id || '',
      trastero_id: item.trastero_id || '',
      tipo: item.tipo,
      prioridad: item.prioridad,
      estado: item.estado,
      descripcion: item.descripcion || '',
      notas_internas: item.notas_internas || ''
    });
    setEditingId(item.id);
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        cliente_id: form.cliente_id || null,
        trastero_id: form.trastero_id || null
      };

      if (editingId) await update(editingId, payload);
      else await insert(payload);
      
      setIsModalOpen(false);
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta incidencia?')) return;
    try {
      await remove(id);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // Ordenar listados para los desplegables
  const sortedTrasteros = [...trasteros].sort((a, b) => (a.codigo || '').localeCompare(b.codigo || '', 'es', { numeric: true }));
  const sortedClientes = [...clientes].sort((a, b) => (a.nombre_completo || '').localeCompare(b.nombre_completo || '', 'es'));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-brand-dark flex items-center gap-2">
            Incidencias
            {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-lg" />}
          </h2>
          <p className="text-brand-text">Gestión de mantenimiento y reportes.</p>
        </div>
        <Button onClick={openNew} className="flex items-center gap-2 bg-brand-alert hover:bg-red-600 text-white">
          <SafeIcon icon={FiIcons.FiPlus} />
          Nueva Incidencia
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3 text-sm">
          <SafeIcon icon={FiIcons.FiAlertTriangle} /><span>Error: {error}</span>
        </div>
      )}

      <Card>
        <div className="p-4 border-b border-gray-100 flex justify-end">
          <Button variant="ghost" onClick={refresh}>
            <SafeIcon icon={FiIcons.FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        <div className="overflow-x-auto">
          {!loading && data.length === 0 ? (
            <p className="text-center text-brand-text py-12 text-sm">No hay incidencias registradas.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-brand-text">
                <tr>
                  <th className="px-6 py-3 font-medium">Título</th>
                  <th className="px-6 py-3 font-medium">Tipo</th>
                  <th className="px-6 py-3 font-medium">Prioridad</th>
                  <th className="px-6 py-3 font-medium">Fecha</th>
                  <th className="px-6 py-3 font-medium">Estado</th>
                  <th className="px-6 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-brand-dark">{item.titulo}</td>
                    <td className="px-6 py-4 text-brand-text capitalize">{item.tipo}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-bold uppercase ${item.prioridad === 'alta' || item.prioridad === 'urgente' ? 'text-brand-alert' : 'text-gray-500'}`}>
                        {item.prioridad}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-brand-text">{item.created_at?.slice(0, 10) || '—'}</td>
                    <td className="px-6 py-4"><Badge variant={item.estado}>{item.estado}</Badge></td>
                    <td className="px-6 py-4 text-right flex items-center justify-end gap-1">
                      <button className="text-gray-400 hover:text-brand-primary p-1" onClick={() => openEdit(item)}>
                        <SafeIcon icon={FiIcons.FiEdit2} />
                      </button>
                      <button className="text-gray-400 hover:text-brand-alert p-1" onClick={() => handleDelete(item.id)}>
                        <SafeIcon icon={FiIcons.FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }}
        title={editingId ? 'Editar Incidencia' : 'Nueva Incidencia'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Título" placeholder="Describe la incidencia brevemente" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Cliente (opcional)</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.cliente_id} onChange={e => setForm({...form, cliente_id: e.target.value})}>
                <option value="">— Sin asignar —</option>
                {sortedClientes.map(c => <option key={c.id} value={c.id}>{c.nombre_completo}</option>)}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Trastero (opcional)</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.trastero_id} onChange={e => setForm({...form, trastero_id: e.target.value})}>
                <option value="">— Sin asignar —</option>
                {sortedTrasteros.map(t => <option key={t.id} value={t.id}>{t.codigo} – {t.zona}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Tipo</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                <option value="acceso">Acceso</option>
                <option value="mantenimiento">Mantenimiento</option>
                <option value="pago">Pago</option>
                <option value="limpieza">Limpieza</option>
                <option value="seguridad">Seguridad</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Prioridad</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.prioridad} onChange={e => setForm({...form, prioridad: e.target.value})}>
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-brand-text mb-1">Estado</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary" value={form.estado} onChange={e => setForm({...form, estado: e.target.value})}>
                <option value="abierta">Abierta</option>
                <option value="revision">Revisión</option>
                <option value="resolviendo">Resolviendo</option>
                <option value="resuelta">Resuelta</option>
                <option value="cerrada">Cerrada</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text mb-1">Descripción</label>
            <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary text-sm" rows={3} placeholder="Detalla la incidencia..." value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text mb-1">Notas internas</label>
            <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-brand-primary text-sm" rows={2} placeholder="Solo visible para el equipo..." value={form.notas_internas} onChange={e => setForm({...form, notas_internas: e.target.value})} />
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
            <Button variant="ghost" onClick={() => { setIsModalOpen(false); setForm(EMPTY); setEditingId(null); }} type="button">Cancelar</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar Incidencia'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Incidencias;